import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit'
import { createSelector } from 'reselect'
import { serverApiFetch } from '../../../services/server-api-client'
import { errorMessage } from '@/lib/utils'

// Types
export interface Business {
  id: string
  name: string
  settings: Record<string, unknown> | null
  createdAt: string
  updatedAt: string
  memberCount: number
  creativeCount: number
}

export interface BusinessDetail extends Business {
  agentConfig?: Record<string, unknown> | null
  creativesByType: Record<string, number>
  /** null = draft (created via add-another-business but never past the paywall). */
  setupCompletedAt?: string | null
}

/**
 * Brand-kit jsonb passthrough from the server. Only the fields the admin
 * Brand tab renders are typed; anything else the server adds stays `unknown`.
 */
export interface BrandKitData {
  businessName?: string
  industry?: string
  contentFocus?: string
  timezone?: string
  description?: string
  targetAudience?: string
  websiteUrl?: string
  /** Resolved to a signed URL by the admin read when possible. */
  logoId?: string
  toneOfVoice?: string | string[]
  goals?: string[]
  keywords?: string[]
  colors?: Record<string, string>
  fonts?: { heading?: string; body?: string }
  images?: Array<{ attachmentId?: string; publicUrl?: string; tags?: string[] }>
  channels?: Record<string, unknown>
  [key: string]: unknown
}

export interface BusinessMember {
  id: string
  userId: string
  role: string
  status: string
  createdAt: string
  /** Where the invite went — set on invited rows (incl. email-only strangers with no user). */
  invitedEmail?: string | null
  /** Invite token expiry — pending rows only. */
  inviteExpiresAt?: string | null
  user: {
    id: string
    name: string
    email: string
    avatar?: { publicUrl: string } | null
  } | null
}

export interface UserBusinessMembership {
  id: string
  role: string
  status: string
  createdAt: string
  business: Business | null
}

interface PaginationMeta {
  page: number
  limit: number
  total: number
  totalPages: number
  hasNextPage: boolean
  hasPreviousPage: boolean
}

/** Per-business members entry — keyed by businessId so a slow response for
 * business A can never render under business B (mirror of userBusinesses). */
export interface BusinessMembersEntry {
  data: BusinessMember[]
  pagination: PaginationMeta | null
  loading: boolean
  error: string | null
  fetched: boolean
}

// The businesses LIST now lives in react-query (hooks/use-admin-businesses.ts)
// — this slice only carries the business-detail page state plus the per-user
// memberships the user-detail page renders.
interface BusinessesState {
  currentBusiness: BusinessDetail | null
  currentBusinessLoading: boolean
  currentBusinessError: string | null

  // Members keyed by businessId (see BusinessMembersEntry).
  businessMembers: Record<string, BusinessMembersEntry>

  brandKit: BrandKitData | null
  brandKitLoading: boolean

  // User's businesses (for UserDetailView)
  userBusinesses: Record<
    string,
    { data: UserBusinessMembership[]; loading: boolean; error: string | null; fetched: boolean }
  >
}

const initialState: BusinessesState = {
  currentBusiness: null,
  currentBusinessLoading: false,
  currentBusinessError: null,

  businessMembers: {},

  brandKit: null,
  brandKitLoading: false,

  userBusinesses: {},
}

// Thunks
export const fetchBusinessById = createAsyncThunk(
  'businesses/fetchById',
  async (businessId: string, { rejectWithValue }) => {
    try {
      return await serverApiFetch<BusinessDetail>(`/v1/admin/businesses/${businessId}`)
    } catch (error) {
      return rejectWithValue(errorMessage(error) || 'Failed to fetch business')
    }
  }
)

export const completeBusinessSetup = createAsyncThunk(
  'businesses/completeSetup',
  async (businessId: string, { rejectWithValue }) => {
    try {
      return await serverApiFetch<{ id: string; setupCompletedAt: string | null }>(
        `/v1/admin/businesses/${businessId}/complete-setup`,
        { method: 'POST' }
      )
    } catch (error) {
      return rejectWithValue(errorMessage(error) || 'Failed to complete setup')
    }
  }
)

export const fetchBusinessMembers = createAsyncThunk(
  'businesses/fetchMembers',
  async (params: { businessId: string; page?: number; limit?: number }, { rejectWithValue }) => {
    try {
      const queryParams = new URLSearchParams()
      queryParams.set('page', (params.page || 1).toString())
      queryParams.set('limit', (params.limit || 20).toString())

      return await serverApiFetch<{
        data: BusinessMember[]
        pagination: PaginationMeta
      }>(`/v1/admin/businesses/${params.businessId}/members?${queryParams.toString()}`)
    } catch (error) {
      return rejectWithValue(errorMessage(error) || 'Failed to fetch members')
    }
  }
)

export const fetchBusinessBrandKit = createAsyncThunk(
  'businesses/fetchBrandKit',
  async (businessId: string, { rejectWithValue }) => {
    try {
      return await serverApiFetch<BrandKitData>(`/v1/admin/businesses/${businessId}/brand-kit`)
    } catch (error) {
      return rejectWithValue(errorMessage(error) || 'Failed to fetch brand kit')
    }
  }
)

export const fetchUserBusinesses = createAsyncThunk(
  'businesses/fetchUserBusinesses',
  async (userId: string, { rejectWithValue }) => {
    try {
      const response = await serverApiFetch<{ data: UserBusinessMembership[] }>(
        `/v1/admin/businesses/user/${userId}`
      )
      return { userId, data: response.data }
    } catch (error) {
      return rejectWithValue({
        message: errorMessage(error) || 'Failed to fetch user businesses',
        userId,
      })
    }
  }
)

// Slice
const businessesSlice = createSlice({
  name: 'businesses',
  initialState,
  reducers: {
    clearCurrentBusiness: state => {
      state.currentBusiness = null
      state.currentBusinessError = null
      state.businessMembers = {}
      state.brandKit = null
    },
    clearUserBusinesses: (state, action: PayloadAction<string>) => {
      delete state.userBusinesses[action.payload]
    },
  },
  extraReducers: builder => {
    builder
      .addCase(fetchBusinessById.pending, state => {
        state.currentBusinessLoading = true
        state.currentBusinessError = null
      })
      .addCase(fetchBusinessById.fulfilled, (state, action) => {
        state.currentBusiness = action.payload
        state.currentBusinessLoading = false
      })
      .addCase(fetchBusinessById.rejected, (state, action) => {
        state.currentBusinessLoading = false
        state.currentBusinessError = action.payload as string
      })
      .addCase(completeBusinessSetup.fulfilled, (state, action) => {
        if (state.currentBusiness?.id === action.payload.id) {
          state.currentBusiness.setupCompletedAt = action.payload.setupCompletedAt
        }
      })

    // Every members reducer writes ONLY the entry for the businessId it was
    // dispatched for (action.meta.arg) — a late response for business A can
    // never land in business B's entry.
    builder
      .addCase(fetchBusinessMembers.pending, (state, action) => {
        const { businessId } = action.meta.arg
        state.businessMembers[businessId] = {
          data: state.businessMembers[businessId]?.data || [],
          pagination: state.businessMembers[businessId]?.pagination || null,
          loading: true,
          error: null,
          fetched: state.businessMembers[businessId]?.fetched || false,
        }
      })
      .addCase(fetchBusinessMembers.fulfilled, (state, action) => {
        const { businessId } = action.meta.arg
        state.businessMembers[businessId] = {
          data: action.payload.data,
          pagination: action.payload.pagination,
          loading: false,
          error: null,
          fetched: true,
        }
      })
      .addCase(fetchBusinessMembers.rejected, (state, action) => {
        const { businessId } = action.meta.arg
        state.businessMembers[businessId] = {
          data: state.businessMembers[businessId]?.data || [],
          pagination: state.businessMembers[businessId]?.pagination || null,
          loading: false,
          error: (action.payload as string) || 'Failed to fetch members',
          fetched: true,
        }
      })

    builder
      .addCase(fetchBusinessBrandKit.pending, state => {
        state.brandKitLoading = true
      })
      .addCase(fetchBusinessBrandKit.fulfilled, (state, action) => {
        state.brandKit = action.payload
        state.brandKitLoading = false
      })
      .addCase(fetchBusinessBrandKit.rejected, state => {
        state.brandKitLoading = false
      })

    builder
      .addCase(fetchUserBusinesses.pending, (state, action) => {
        const userId = action.meta.arg
        state.userBusinesses[userId] = {
          data: state.userBusinesses[userId]?.data || [],
          loading: true,
          error: null,
          fetched: state.userBusinesses[userId]?.fetched || false,
        }
      })
      .addCase(fetchUserBusinesses.fulfilled, (state, action) => {
        const { userId, data } = action.payload
        state.userBusinesses[userId] = { data, loading: false, error: null, fetched: true }
      })
      .addCase(fetchUserBusinesses.rejected, (state, action) => {
        const { userId, message } = action.payload as { userId: string; message: string }
        if (state.userBusinesses[userId]) {
          state.userBusinesses[userId].loading = false
          state.userBusinesses[userId].error = message
          state.userBusinesses[userId].fetched = true
        }
      })
  },
})

export const { clearCurrentBusiness, clearUserBusinesses } = businessesSlice.actions
export const businessesReducer = businessesSlice.reducer
export default businessesSlice.reducer

// Selectors
const selectBusinessesState = (state: { businesses: BusinessesState }) => state.businesses

export const selectCurrentBusiness = createSelector(
  [selectBusinessesState],
  state => state.currentBusiness
)

export const selectCurrentBusinessLoading = createSelector(
  [selectBusinessesState],
  state => state.currentBusinessLoading
)

export const selectCurrentBusinessError = createSelector(
  [selectBusinessesState],
  state => state.currentBusinessError
)

const EMPTY_MEMBERS: BusinessMember[] = []

// Members selectors are factories keyed by businessId (same pattern as
// billing.slice's selectBusinessBilling) — consumers memoize per id.
export const selectBusinessMembers = (businessId: string) =>
  createSelector(
    [selectBusinessesState],
    state => state.businessMembers[businessId]?.data ?? EMPTY_MEMBERS
  )

export const selectBusinessMembersLoading = (businessId: string) =>
  createSelector(
    [selectBusinessesState],
    state => state.businessMembers[businessId]?.loading ?? false
  )

export const selectBusinessMembersError = (businessId: string) =>
  createSelector([selectBusinessesState], state => state.businessMembers[businessId]?.error ?? null)

export const selectBusinessMembersPagination = (businessId: string) =>
  createSelector(
    [selectBusinessesState],
    state => state.businessMembers[businessId]?.pagination ?? null
  )

export const selectUserBusinesses = createSelector(
  [selectBusinessesState, (_state: { businesses: BusinessesState }, userId: string) => userId],
  (state, userId) => state.userBusinesses[userId]?.data || []
)

export const selectUserBusinessesLoading = createSelector(
  [selectBusinessesState, (_state: { businesses: BusinessesState }, userId: string) => userId],
  (state, userId) => state.userBusinesses[userId]?.loading || false
)

export const selectUserBusinessesFetched = createSelector(
  [selectBusinessesState, (_state: { businesses: BusinessesState }, userId: string) => userId],
  (state, userId) => state.userBusinesses[userId]?.fetched || false
)
